/**
 * 🔒 MyContextSharesTab — Onglet user "Mon contexte partagé"
 *
 * Liste tous les partages (actifs/expirés/révoqués) avec synthèse read-only
 * (transparence totale) + révocation.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Shield, RefreshCw, AlertCircle, CheckCircle2, Clock, XCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';
import MyPersonalContextCard from './MyPersonalContextCard';

const T = {
  bg: '#FBF9F3', card: '#FFFFFF', ink: '#1C1147', text: '#1F2937',
  muted: '#6B7280', border: '#E8E2D2', gold: '#D4AF37', goldDeep: '#B7901F',
  ivoryDeep: '#FFFCEC', green: '#48825A', red: '#9B2C1F', amber: '#B7901F',
};

const STATUS_LABEL = {
  active: { label: 'Actif', color: T.green, icon: CheckCircle2 },
  expired: { label: 'Expiré', color: T.muted, icon: Clock },
  revoked: { label: 'Révoqué', color: T.red, icon: XCircle },
};

const SCOPE_LABEL = {
  A: 'Synthèse courte',
  B: 'Synthèse + objectifs + critères',
  C: 'Synthèse + rapports IA',
  D: 'Personnalisé',
};

const CATEGORY_LABEL = {
  context_summary: 'Contexte du parcours',
  global_progress: 'Progression globale',
  objectives: 'Objectifs',
  validated_criteria: 'Critères validés',
  non_validated_criteria: 'Critères non validés',
  evidences: 'Preuves agrégées',
  progress_actions: 'Actions de progrès',
  ai_reports: 'Rapports IA',
  recurring_difficulties: 'Difficultés récurrentes',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return iso.slice(0,10); }
};

function SynthesisBlock({ s }) {
  if (!s) return null;
  return (
    <div
      className="p-4 mt-3 rounded-xl text-sm space-y-3"
      style={{ background: T.ivoryDeep, border: `1px solid ${T.border}`, color: T.text }}
      data-testid="my-share-synthesis"
    >
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{color: T.goldDeep}}>
          Contexte du parcours
        </div>
        <div>{s.contexte_parcours}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{color: T.goldDeep}}>
          Objectifs travaillés
        </div>
        <ul className="list-disc pl-4">
          {(s.objectifs_travailles || []).map((o, i) => <li key={i}>{o}</li>)}
        </ul>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{color: T.goldDeep}}>
          Preuves observées
        </div>
        <ul className="list-disc pl-4">
          {(s.preuves_observees || []).map((o, i) => <li key={i}>{o}</li>)}
        </ul>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{color: T.goldDeep}}>
          Points de vigilance
        </div>
        <ul className="list-disc pl-4">
          {(s.points_vigilance || []).map((o, i) => <li key={i}>{o}</li>)}
        </ul>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{color: T.goldDeep}}>
          Action de progrès
        </div>
        <div>{s.action_progres}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{color: T.goldDeep}}>
          Recommandation coach
        </div>
        <div>{s.recommandation_coach}</div>
      </div>
    </div>
  );
}

export default function MyContextSharesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const { token } = useAuth();
  const API = getApiUrl();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/coaching-context/my-shares`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setItems(j.items || []);
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const revoke = async (shareId) => {
    if (!window.confirm('Révoquer ce partage ? Le coach ne pourra plus y accéder.')) return;
    try {
      const r = await fetch(`${API}/coaching-context/${shareId}/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Partage révoqué.');
      fetchData();
    } catch (e) {
      toast.error(`Échec : ${e.message}`);
    }
  };

  return (
    <div className="space-y-7" data-testid="my-context-tab-root">
      {/* 🌅 1. Hero "Mon contexte personnel actuel" — synthèse premium */}
      <MyPersonalContextCard />

      {/* 🔒 2. Partages RGPD coach humain (logique existante préservée) */}
      <div
        data-testid="my-context-shares-tab"
        className="rounded-2xl p-5 sm:p-7 space-y-5"
        style={{ background: T.bg, border: `1px solid ${T.border}` }}
      >
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="uppercase text-[11px] tracking-[0.22em] mb-1 font-semibold" style={{color: T.goldDeep}}>
              RGPD · Vos partages coach
            </p>
            <h2
              className="text-xl sm:text-2xl"
              style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 600 }}
            >
              Partages avec un coach humain
            </h2>
            <p className="text-xs mt-1" style={{color: T.muted}}>
              Voyez exactement ce que voit votre coach · Révocation immédiate possible
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            data-testid="my-context-shares-refresh-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold"
            style={{ background: T.card, color: T.ink, border: `1px solid ${T.border}` }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </button>
        </div>

      {!loading && items.length === 0 && (
        <div data-testid="my-context-shares-empty"
          className="p-10 rounded-2xl text-center"
          style={{ background: T.card, border: `1px dashed ${T.border}` }}
        >
          <Shield className="w-10 h-10 mx-auto mb-3" style={{ color: T.goldDeep }} />
          <p className="text-sm" style={{ color: T.text, fontFamily: 'Georgia, serif', fontWeight: 600 }}>
            Aucun partage de contexte pour le moment.
          </p>
          <p className="text-xs mt-1" style={{ color: T.muted }}>
            Lors de votre prochaine réservation coach, vous pourrez choisir de partager votre contexte Owen.
          </p>
        </div>
      )}

      <div className="space-y-2.5" data-testid="my-context-shares-list">
        {items.map(({ share, summary }) => {
          const statusMeta = STATUS_LABEL[share.status] || STATUS_LABEL.active;
          const StatusIcon = statusMeta.icon;
          const isOpen = openId === share.share_id;
          return (
            <motion.div
              key={share.share_id}
              initial={{opacity:0, y:4}} animate={{opacity:1, y:0}}
              data-testid={`my-share-row-${share.share_id}`}
              className="p-4 rounded-xl" style={{ background: T.card, border: `1px solid ${T.border}` }}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <StatusIcon className="w-4 h-4" style={{ color: statusMeta.color }} />
                    <span className="text-xs font-semibold" style={{ color: statusMeta.color }}>
                      {statusMeta.label}
                    </span>
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold"
                      style={{ background: T.ivoryDeep, color: T.goldDeep, border: `1px solid ${T.border}` }}>
                      Scope {share.consent_scope} · {SCOPE_LABEL[share.consent_scope]}
                    </span>
                  </div>
                  <div className="text-sm" style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 600 }}>
                    Coach ID : <span className="font-mono text-xs">{share.coach_user_id}</span>
                  </div>
                  <div className="text-xs mt-1 flex flex-wrap gap-x-3 gap-y-1" style={{ color: T.muted }}>
                    <span>Autorisé le {fmtDate(share.consented_at)}</span>
                    <span>· Expire le {fmtDate(share.expires_at)}</span>
                    <span>· {(share.allowed_categories || []).length} catégorie(s)</span>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => setOpenId(isOpen ? null : share.share_id)}
                    data-testid={`my-share-toggle-${share.share_id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs"
                    style={{ background: T.card, color: T.ink, border: `1px solid ${T.border}` }}
                  >
                    <Eye className="w-3 h-3" /> {isOpen ? 'Masquer' : 'Voir synthèse'}
                  </button>
                  {share.status === 'active' && (
                    <button
                      onClick={() => revoke(share.share_id)}
                      data-testid={`my-share-revoke-${share.share_id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
                      style={{ background: '#FFF4F2', color: T.red, border: `1px solid #F8C9C0` }}
                    >
                      Révoquer
                    </button>
                  )}
                </div>
              </div>
              {isOpen && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1" style={{color: T.goldDeep}}>
                    Catégories autorisées
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(share.allowed_categories || []).map(c => (
                      <span key={c} className="px-2 py-0.5 rounded text-[10px]"
                        style={{ background: T.ivoryDeep, color: T.text, border: `1px solid ${T.border}` }}>
                        {CATEGORY_LABEL[c] || c}
                      </span>
                    ))}
                  </div>
                  {summary ? (
                    <SynthesisBlock s={summary.synthesis} />
                  ) : (
                    <div className="mt-3 p-3 rounded-lg text-xs flex items-center gap-2"
                      style={{ background: '#FFF8E0', color: T.amber, border: `1px solid ${T.border}` }}>
                      <AlertCircle className="w-4 h-4" /> Synthèse en cours de génération…
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      <p className="text-[10px] text-center pt-2" style={{ color: T.muted }}>
        🔒 Vos données sont partagées selon votre consentement explicite · Conservation limitée · Révocation immédiate
      </p>
      </div>
    </div>
  );
}
